import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import config from '../config';
import styled from 'styled-components';
import RemoveTrailingSlash from '../helpers/RemoveTrailingSlash';
import Cookies from 'js-cookie';
import { COLOR, FONT } from '../theme/constants';
import { trimTrailingSlash } from '@emarketer/code-helpers';
import { Branding, Footer, IRightRailEntry } from '@emarketer/componentlibrary';
import { useGAM, IAdSlotConfig, TestAdContainer } from '@emarketer/googleadmanager';
import { DataLayerProps, useDataLayer } from '@emarketer/datalayer';
import { IServiceDebugInfo, isShowServiceDebugInfo, showServiceDebugInfo } from '@emarketer/debuginfo';
import { getLoginButtonLink, useAuthVerification, useUser } from '@emarketer/auth';
import { GetAuthConfig } from '../auth/AuthConfig';

import '@emarketer/componentlibrary/build/index.css'; // !IMPORTANT for ComponentLibrary after migration from "Styled Components" to "CSS Modules"

interface LayoutProps {
  children: JSX.Element | JSX.Element[] | (JSX.Element | null)[];
  title?: string;
  description?: string;
  url?: string;
  canonicalUrl?: string;
  width?: string;
  brandingProps?: object;
  bgColor?: string;
  adSlotsConfig: IAdSlotConfig[];
  brandingAssets: IRightRailEntry[];
  topics?: string[];
  publicationDate?: string;
  lastModifiedTimestamp?: string;
  dataLayerProps: DataLayerProps;
  serviceDependenciesDebugInfo?: IServiceDebugInfo[];
}

const Layout: React.FunctionComponent<LayoutProps> = ({
  children,
  title = 'EMARKETER',
  description = '',
  url = config.publicPath,
  canonicalUrl = '',
  width = '',
  brandingProps = { isTransparent: false, enablePodRedirection: true },
  bgColor = '',
  adSlotsConfig,
  brandingAssets,
  topics,
  publicationDate,
  lastModifiedTimestamp,
  dataLayerProps,
  serviceDependenciesDebugInfo,
}) => {
  const { user } = useUser();

  const authConfig = GetAuthConfig({
    publicForecastsServiceUrl: config.forecastsServiceUrl,
    loggedInForecastsServiceUrl: config.forecastsSubscriptionServiceUrl,
    currentEnvironment: config.currentEnvironment,
  });
  //#region LOGIN URL
  const [logInUrl, setLogInUrl] = useState('');
  useEffect(() => {
    const urlForLoginButton = getLoginButtonLink(config.subscriptionsLoginUrl, authConfig);
    setLogInUrl(urlForLoginButton);
  }, []);
  //#endregion LOGIN URL

  //#region AUTHENTICATION
  // NOTE: there's a matching logic on the back-end (expressjs), but we have it here too in case page gets cached
  useAuthVerification({
    emService: authConfig,
    emReAuthenticationServiceBaseUrl: config.subscriptionsLoginUrl,
    // logger: { loggerInstance: console, logLevel: 'debug' },
  });
  //#endregion AUTHENTICATION

  // DATA LAYER
  useDataLayer(dataLayerProps);
  useGAM(
    {
      gptScriptUrl: config.gamGptScriptUrl,
      microserviceS1Id: config.gamCurrentMicroserviceS1Id,
      networkId: config.gamNetworkId,
      parentAdUnitId: config.gamParentAdUnitId,
      testAdContainer: config.gamTestAdContainerId as TestAdContainer,
      adSlots: adSlotsConfig,
      userEmailDomain: Cookies.get(config.userEmailDomainCookieKey),
      pageTopic: topics,
      userAccountTier: user.subscriptionTier,
    },
    config.currentEnvironment,
  );

  useEffect(() => {
    //#region DEBUG-INFO
    // Expose service & components version/debug info if non-prod environment
    isShowServiceDebugInfo(config.currentEnvironment) &&
      showServiceDebugInfo(
        {
          serviceName: config.serviceName || '',
          releaseVersion: config.debugVersion || '',
        },
        serviceDependenciesDebugInfo,
      );
    //#endregion DEBUG-INFO
  }, []);

  //#region JSON+LD schema
  const jsonLdSchema: any = {
    '@context': 'http://schema.org',
    '@type': 'WebPage',
    headline: `${title}`,
    name: `${title}`,
    description: `${description}`,
    url: `${RemoveTrailingSlash(url)}`,
    //datePublished: .. might not exist for some pages see below
    //dateModified: .. might not exist for some pages, see below
    image: {
      '@type': 'ImageObject',
      url: `${config.defaultShareUrl}`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'EMARKETER',
      logo: {
        '@type': 'ImageObject',
        url: `${config.ldJsonLogoUrl}`,
      },
    },
  };
  if (publicationDate) {
    jsonLdSchema.datePublished = `${publicationDate}`;
  }
  if (lastModifiedTimestamp || publicationDate) {
    jsonLdSchema.dateModified = `${lastModifiedTimestamp || publicationDate}`;
  }

  //#endregion JSON+LD schema
  return (
    <>
      <Branding
        articlesServiceUrl={config.articlesServiceUrl}
        emHomeUrl={config.iiHomeUrl}
        researchTopicsServiceUrl={config.researchTopicsServiceUrl}
        searchServiceUrl={config.searchServiceUrl}
        noteworthyContentInjectable={config.noteworthyContentService}
        noteworthyContentApiUrl={config.noteworthyContentApi}
        typeaheadPluginUrl={config.typeaheadPluginUrl}
        loginUrl={logInUrl}
        osanoScriptUrl={config.osanoScriptUrl}
        gaApiSecret={config.gaApiSecret}
        gaMeasurementId={config.gaMeasurementId}
        doesMyCompanyURL={config.doesmyCompanyPluginUrl}
        coverageRightRailItems={brandingAssets}
        {...brandingProps}
      />
      <LayoutWrapper width={width}>
        <Head>
          <link rel="shortcut icon" href={`${trimTrailingSlash(config.publicPath)}/favicon.ico`} />
          {config.robotsNoIndex ? (
            <meta name="robots" content="none, noimageindex, noarchive, nocache, nosnippet, max-image-preview:large" />
          ) : (
            <meta name="robots" content="max-image-preview:large" />
          )}
          <meta charSet="utf-8" />
          <meta name="viewport" content="initial-scale=1.0, width=device-width" />
          <title>{`${title}`}</title>
          <meta property="og:title" content={title}></meta>
          <meta property="og:site_name" content="EMARKETER" />
          <meta property="og:url" content={url} />
          <link rel="canonical" href={`${canonicalUrl}/`} />
          <meta name="description" content={description} />
          <meta property="og:type" content="article" />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={`${config.defaultShareUrl}`} />
          <meta property="og:image:width" content="1200" />
          <meta name="twitter:url" content={url} />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta property="article:publisher" content="https://www.facebook.com/InsiderIntelligence" />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(jsonLdSchema),
            }}
          ></script>
          <script
            type="text/javascript"
            dangerouslySetInnerHTML={{
              __html:
                'if(typeof window!=="undefined"){var iiAna=window.iiAna=window.iiAna||function iiAnaShim(){(iiAna.q=iiAna.q||[]).push(arguments);}}',
            }}
          ></script>

          <script async type="text/javascript" src={config.analyticsServiceUrl}></script>
          <link rel="stylesheet" href={config.fontCSSUrl} />
        </Head>
        <ChildWrapper bgColor={bgColor}>{children}</ChildWrapper>
      </LayoutWrapper>

      <Footer
        articlesServiceUrl={config.articlesServiceUrl}
        contentUrl={config.contentUrl}
        emHomeUrl={config.iiHomeUrl}
        numbersUrl={config.forecastsServiceUrl}
        researchTopicsServiceUrl={config.researchTopicsServiceUrl}
        searchServiceUrl={config.searchServiceUrl}
        osfScriptUrl={config.osfScriptUrl}
      />
    </>
  );
};

const LayoutWrapper = styled.div<{ width: string }>`
  width: auto;
  margin: 0 auto;
  margin-top: 0px;
  background-color: ${COLOR.BACKGROUND};
  font-family: ${FONT.FAMILYLABG};

  @media (min-width: 991px) {
    width: ${({ width }) => (width === 'full' ? '100%' : '950px')};
  }

  @media (min-width: 1250px) {
    width: ${({ width }) => (width === 'full' ? '100%' : '1130px')};
  }
`;

const ChildWrapper = styled.div<{ bgColor: string }>`
  min-height: 100vh;
  margin: auto;
  background-color: ${({ bgColor }) => (bgColor ? bgColor : COLOR.WHITE)};
`;

export default Layout;
